import axios, { isAxiosError } from "axios";
import React, { useCallback, useEffect, useMemo, useState, type ChangeEvent } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import apiRoutes from "../../../../constants/api-routes";
import messageLevels from "../../../../constants/message-levels";
import messages from "../../../../constants/messages";
import navigationItemReferences from "../../../../constants/navigation-item-references";
import pages from "../../../../constants/pages";
import useAuthHeader from "../../../../hooks/useAuthHeader";
import useCoAppNavigation from "../../../../hooks/useCoAppNavigation";
import useDatagridSorting from "../../../../hooks/useDatagridSorting";
import useToastAlert from "../../../../hooks/useToastAlert";
import { selectOrganizationIdentityProvider, selectUser } from "../../../../redux/userSlice";
import CoAppDatagridHeader from "../../../global/components/datagrid/CoAppDatagridHeader";
import useCoAppDataGridFilters from "../../../global/components/datagrid/useCoAppDataGridFilters";
import CoAppStandardDataGrid from "../../../global/components/datagrid/CoAppStandardDataGrid";
import CoAppAddItemsToListModal from "../../../global/components/modals/CoAppAddItemsToListModal";
import CoAppDestructiveConfirmationModal from "../../../global/components/modals/CoAppDestructiveConfirmationModal";
import CoAppEditNameModal from "../../../global/components/modals/CoAppEditNameModal";
import { getRoleSSOColumns, type RoleIdpGroupRow } from "./columns/role-sso-columns";

/** Passed from `RoleWithErrorBoundary` when the route was opened without `navigate(..., { state })` (e.g. sidebar). */
interface RoleNavState {
	name: string;
	isSuperAdmin?: boolean;
}

interface RoleSSOProps {
	state?: RoleNavState | null;
}

interface ExternalGroup {
	externalGroupId: string;
	externalGroupName: string;
}

interface IdpGroup {
	externalGroupId: string;
	name: string;
}

export default function RoleSSO({ state: stateFromBoundary }: RoleSSOProps) {
	const authHeader = useAuthHeader();
	const identityProviderName = useSelector(selectOrganizationIdentityProvider);
	const currentUser = useSelector(selectUser);
	const { updateCoAppNavigation } = useCoAppNavigation();
	const { sortModel, handleSortModelChange, handleInitializeSortModel } = useDatagridSorting("roleIdpGroups");
	const { handleToastAlert } = useToastAlert();
	const navigate = useNavigate();
	const { roleId: resolvedRoleId } = useParams<{ roleId: string }>();
	const { pathname, state: stateFromLocation } = useLocation() as {
		pathname: string;
		state: RoleNavState | undefined;
	};
	/* Router `location.state` only exists when navigation passes `{ state }`. Otherwise the parent boundary injects `state` after fetching the role. */
	const state = (stateFromBoundary ?? stateFromLocation) as RoleNavState;

	const [roleIdpGroups, setRoleIdPGroups] = useState<RoleIdpGroupRow[]>([]);
	const [idpGroupsModalOpen, setIdpGroupsModalOpen] = useState(false);
	const [availableIdpGroups, setAvailableIdpGroups] = useState<IdpGroup[]>([]);
	const [idpGroupsToAdd, setIdpGroupsToAdd] = useState<IdpGroup[]>([]);
	const [deleteRoleDialogOpen, setDeleteRoleDialogOpen] = useState(false);
	const [editRoleDialogOpen, setEditRoleDialogOpen] = useState(false);
	const [roleNameInput, setRoleNameInput] = useState("");
	const [roleNameError, setRoleNameError] = useState("");
	const [loading, setLoading] = useState(true);
	const [optionsLoading, setOptionsLoading] = useState(true);
	
	const idpLabel = identityProviderName ?? "SSO";

	const init = useCallback(() => {
		if (!resolvedRoleId) return;
		axios.get(apiRoutes.getRoleIdpGroups(resolvedRoleId), { headers: authHeader })
			.then((res) => {
				setRoleIdPGroups(res.data);
				setLoading(false);
			})
			.catch((err) => {
				console.log(err);
			});
	}, [resolvedRoleId, authHeader]);

	const removeIdPGroupFromRole = useCallback((idpGroupId: RoleIdpGroupRow["id"]) => {
		if (!resolvedRoleId) return;
		axios.delete(apiRoutes.removeIdpGroupFromRole(resolvedRoleId, idpGroupId), { headers: authHeader })
			.then(() => {
				init();
			})
			.catch((err) => {
				console.log(err);
				handleToastAlert(messageLevels.ERROR, messages.IDP_GROUP_REMOVAL_ERROR_MSG(idpLabel));
			});
	}, [resolvedRoleId, authHeader, init, handleToastAlert, idpLabel]);

	const columns = useMemo(() => (
		getRoleSSOColumns(
			identityProviderName,
			removeIdPGroupFromRole
		)
	), [identityProviderName, removeIdPGroupFromRole]);

	const toggleDeleteRoleDialog = () => {
		setDeleteRoleDialogOpen(!deleteRoleDialogOpen);
	};

	const deleteRole = () => {
		if (!resolvedRoleId) return;
		axios.delete(apiRoutes.deleteRole(resolvedRoleId), {
			headers: authHeader
		}).then(() => {
			updateCoAppNavigation(true, navigationItemReferences.roleNavItemIndex);
			navigate(pages.roleManagement, { replace: true });
			toggleDeleteRoleDialog();
		}).catch((err) => {
			console.log(err);
			handleToastAlert(messageLevels.ERROR, messages.ROLE_DELETION_ERROR_MSG);
			toggleDeleteRoleDialog();
		});
	};

	const toggleEditRoleDialog = () => {
		setEditRoleDialogOpen(!editRoleDialogOpen);
		setRoleNameInput("");
		setRoleNameError("");
	};

	const handleRoleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
		setRoleNameInput(e.target.value);
		setRoleNameError("");
	};

	const handleRoleNameChangeSubmit = () => {
		if (!roleNameInput || !resolvedRoleId) return;

		if (roleNameInput === state.name) {
			toggleEditRoleDialog();
			return;
		}

		axios.put(apiRoutes.updateRole(resolvedRoleId), { name: roleNameInput }, { headers: authHeader })
			.then(() => {
				toggleEditRoleDialog();
				updateCoAppNavigation(false, navigationItemReferences.roleNavItemIndex, roleNameInput, pathname);
			})
			.catch((e: unknown) => {
				console.log(e);
				if (
					isAxiosError(e)
					&& typeof e.response?.data === "object"
					&& e.response.data !== null
					&& "message" in e.response.data
					&& typeof (e.response.data as { message?: string }).message === "string"
					&& (e.response.data as { message: string }).message.includes("already exists")
				) {
					setRoleNameError(messages.RESOURCE_NAME_ALREADY_EXISTS_ERROR_MSG("Role"));
					return;
				}
				toggleEditRoleDialog();
				handleToastAlert(messageLevels.ERROR, messages.ROLE_NAME_UPDATE_ERROR_MSG);
			});
	};

	const toggleIdpGroupsModal = () => {
		setIdpGroupsModalOpen(!idpGroupsModalOpen);
		
		if (!idpGroupsModalOpen) {
			initIdpGroups();
			setIdpGroupsModalOpen(true);
		} else {
			setIdpGroupsModalOpen(false);
			setIdpGroupsToAdd([]);
		}
	};

	const addIdPGroupToRole = () => {
		if (idpGroupsToAdd.length === 0 || !resolvedRoleId) return;
		axios.put(apiRoutes.addIdpGroupsToRole(resolvedRoleId), { idpgroups: idpGroupsToAdd }, { headers: authHeader })
			.then(() => {
				init();
				toggleIdpGroupsModal();
			})
			.catch((error) => {
				console.log(error);
			});
	};

	const initAvailableIdpGroups = (groups: ExternalGroup[]) => {
		setOptionsLoading(true);
		const available: IdpGroup[] = [];
		if (groups && groups.length > 0) {
			groups.forEach((idpGroup) => {
				if (!roleIdpGroups || !roleIdpGroups.some((roleIdpGroup) => roleIdpGroup.name === idpGroup.externalGroupName)) {
					available.push({ externalGroupId: idpGroup.externalGroupId, name: idpGroup.externalGroupName });
				}
			});
		}
		setAvailableIdpGroups(available);
		setOptionsLoading(false);
	};

	const initIdpGroups = () => {
		const orgId = currentUser.organizationId;
		if (orgId == null) return;
		axios.get(apiRoutes.getExternalGroups(orgId), { headers: authHeader })
			.then((res) => {
				initAvailableIdpGroups(res.data as ExternalGroup[]);
			})
			.catch((err) => {
				console.log(err);
			});
	};

	const addIdPGroupToIdPGroupsToAdd = (value: readonly string[]) => {
		const valueWithIds = [...value]
			.map((groupName) => availableIdpGroups.find((group) => group.name === groupName))
			.filter((g): g is IdpGroup => g != null);
		setIdpGroupsToAdd(valueWithIds);
	};

	useEffect(() => {
		if (!resolvedRoleId) return;
		init();
		handleInitializeSortModel();
	}, [resolvedRoleId, state.name]);

	const {
		apiRef,
		gridFilterModel,
		onFilterModelChange,
		quickFilterSearchProps,
		visibleRowCount,
		filterControl,
		filterButtonEl,
	} = useCoAppDataGridFilters(roleIdpGroups, loading);

	if (!resolvedRoleId) {
		return null;
	}

	return (
		<>
			<CoAppDatagridHeader
				title={`${state.name} SSO Management`}
				resourceType="Role"
				subResourceType={`${identityProviderName ?? "SSO"} Groups`}
				searchIsPresent={true}
				{...quickFilterSearchProps}
				searchPlaceholder="Search all columns…"
				filterControl={filterControl}
				addIsPresent={true}
				addOnClickHandler={toggleIdpGroupsModal}
				editIsPresent={!state.isSuperAdmin}
				editOnClickHandler={toggleEditRoleDialog}
				deleteIsPresent={!state.isSuperAdmin}
				deleteOnClickHandler={toggleDeleteRoleDialog}
			/>
			<CoAppStandardDataGrid
				apiRef={apiRef}
				filterButtonEl={filterButtonEl}
				filterModel={gridFilterModel}
				onFilterModelChange={onFilterModelChange}
				visibleRowCount={visibleRowCount}
				columns={columns}
				rows={roleIdpGroups}
				pinnedColumns={["actions"]}
				allowSelection={false}
				targetResource="SSO"
				parentResource="Role"
				parentResourceName={state.name}
				loading={loading}
				sortModel={sortModel}
				handleSortModelChange={handleSortModelChange}
			/>
			<CoAppDestructiveConfirmationModal
				dialogOpen={deleteRoleDialogOpen}
				dialogTitle={`Delete ${state.name} role?`}
				dialogMessage={messages.DELETION_CONFIRMATION_MSG(state.name, "role")}
				confirmClickHandler={deleteRole}
				cancelClickHandler={toggleDeleteRoleDialog}
				actionText="Delete"
			/>
			<CoAppEditNameModal
				dialogOpen={editRoleDialogOpen}
				dialogTitle="Edit role name"
				changeHandler={handleRoleNameChange}
				placeholderText={state.name}
				confirmClickHandler={handleRoleNameChangeSubmit}
				cancelClickHandler={toggleEditRoleDialog}
				actionText="Save"
				editNameError={roleNameError}
				setEditNameError={setRoleNameError}
			/>
			{
				identityProviderName && (
					<CoAppAddItemsToListModal
						addButtonTitle="Save"
						inputLabel={identityProviderName + " Group(s)"}
						addItemsIsToggled={idpGroupsModalOpen}
						addItemsToCollection={addIdPGroupToRole}
						addItemsToNewArray={addIdPGroupToIdPGroupsToAdd}
						dialogTitle={`Add ${identityProviderName} group(s) to ${state.name}`}
						items={availableIdpGroups}
						itemsToAdd={idpGroupsToAdd}
						toggleDialog={toggleIdpGroupsModal}
						loading={optionsLoading}
						valueLabelField="name"
					/>
				)
			}
		</>
	);
}